import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diff / 60000);
    const hours = Math.round(diff / 3600000);
    const days = Math.round(diff / 86400000);
    if (diff < 60000) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 48) return `${hours}h ago`;
    return `${days}d ago`;
  } catch { return ''; }
};

const ABHIntelPulse = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(`${API}/abh/pulse`);
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Unable to load Intel Pulse');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div
        data-testid="abh-intel-pulse"
        className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
      >
        <p className="text-slate-500 text-sm">Loading Intel Pulse…</p>
      </div>
    );
  }

  const launches = data?.competitor_launches || { items: [], count: 0 };
  const news = data?.top_news || { top: null };
  const influencers = data?.new_influencers || { count: 0, by_platform: {} };

  return (
    <div
      data-testid="abh-intel-pulse"
      className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 space-y-5"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold text-base">ABH Intel Pulse</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Competitor launches · Top GLP-1 news · New influencers
          </p>
        </div>
        <button
          data-testid="abh-intel-pulse-refresh"
          onClick={load}
          className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-300 hover:bg-slate-700"
        >
          Refresh
        </button>
      </div>

      {err && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {err}
        </div>
      )}

      {/* Panel 1 — Competitor Launches */}
      <section data-testid="abh-pulse-competitor-launches">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs uppercase tracking-wider text-slate-400">
            Competitor launches · 24h
          </p>
          <span className="text-xs text-slate-500">{launches.count}</span>
        </div>
        {launches.count === 0 ? (
          <p className="text-xs text-slate-500 italic">No new launches in the last 24h.</p>
        ) : (
          <ul className="space-y-1.5">
            {launches.items.slice(0, 5).map((c, i) => (
              <li
                key={c.competitor_id || i}
                data-testid={`abh-pulse-competitor-${i}`}
                className="flex items-center justify-between gap-3 text-sm bg-slate-800/40 rounded-md px-3 py-2"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-white truncate">{c.name || 'Unknown'}</p>
                  <p className="text-xs text-slate-500">
                    {c.platform}
                    {c.event === 'NEW' ? (
                      <span className="ml-2 text-emerald-400">NEW</span>
                    ) : (
                      <span className="ml-2 text-amber-400">
                        {c.previous_activity_level}→{c.activity_level}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/abh/competitors/${c.competitor_id}`)}
                  className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-200 hover:bg-slate-600"
                >
                  View
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Panel 2 — Top GLP-1 News */}
      <section data-testid="abh-pulse-top-news">
        <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">
          Top GLP-1 news · 72h
        </p>
        {!news.top ? (
          <p className="text-xs text-slate-500 italic">No relevant news in the last 72h.</p>
        ) : (
          <div className="bg-slate-800/40 rounded-md px-3 py-2 space-y-1.5">
            <p className="text-sm text-white leading-tight">{news.top.headline}</p>
            {news.top.summary && (
              <p className="text-xs text-slate-400">{news.top.summary}</p>
            )}
            <div className="flex items-center justify-between pt-1">
              <p className="text-xs text-slate-500">
                {news.top.source} · {formatRelative(news.top.published_at)}
                <span className="ml-2 text-emerald-400">
                  score {(news.top.relevance_score ?? 0).toFixed(2)}
                </span>
              </p>
              {news.top.url && (
                <a
                  data-testid="abh-pulse-news-open"
                  href={news.top.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-emerald-400 hover:text-emerald-300"
                >
                  Open ↗
                </a>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Panel 3 — New Flagged Influencers */}
      <section data-testid="abh-pulse-new-influencers">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs uppercase tracking-wider text-slate-400">
            New flagged influencers · 7d
          </p>
          <span className="text-xs text-slate-500">{influencers.count}</span>
        </div>
        {influencers.count === 0 ? (
          <p className="text-xs text-slate-500 italic">No new flags this week.</p>
        ) : (
          <div className="flex items-center justify-between bg-slate-800/40 rounded-md px-3 py-2">
            <div className="flex items-center gap-3 text-sm text-slate-300">
              {Object.entries(influencers.by_platform).map(([p, n]) => (
                <span key={p} className="text-xs">
                  <span className="text-white font-medium">{n}</span>{' '}
                  <span className="text-slate-500">{p.toLowerCase()}</span>
                </span>
              ))}
            </div>
            <button
              data-testid="abh-pulse-review-influencers"
              onClick={() => navigate('/abh/influencers')}
              className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-200 hover:bg-slate-600"
            >
              Review
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default ABHIntelPulse;
